import { Box, Chip, Tooltip, Typography, alpha } from "@mui/material";
import React from "react";
import {
	RECIPIENT_TYPE_COLOR,
	type RecipientType,
} from "../models/EmailAddressTopic";

const RECIPIENT_ORDER: RecipientType[] = ["TO", "CC", "BCC"];

export type RecipientValues = Partial<Record<RecipientType, string | null>>;

const getGroups = (values: RecipientValues, maxVisible: number) => {
	const groups = RECIPIENT_ORDER.map((type) => ({
		type,
		items:
			values[type]
				?.split(",")
				.map((item) => item.trim())
				.filter(Boolean) ?? [],
	})).filter((group) => group.items.length > 0);

	const shownGroups: { type: RecipientType; items: string[] }[] = [];
	const hiddenGroups: { type: RecipientType; items: string[] }[] = [];
	let remaining = maxVisible;

	for (const group of groups) {
		const shown = group.items.slice(0, Math.max(0, remaining));
		const rest = group.items.slice(shown.length);
		remaining -= shown.length;

		if (shown.length > 0) {
			shownGroups.push({ type: group.type, items: shown });
		}

		if (rest.length > 0) {
			hiddenGroups.push({ type: group.type, items: rest });
		}
	}

	const hidden = hiddenGroups.reduce(
		(sum, group) => sum + group.items.length,
		0,
	);

	return { groups: shownGroups, hiddenGroups, hidden };
};

export const getRecipientText = (
	values: RecipientValues,
	maxVisible: number,
) => {
	const { groups, hidden } = getGroups(values, maxVisible);

	if (groups.length === 0) {
		return "-";
	}

	const text = groups
		.map((group) => `${group.type}: ${group.items.join(", ")}`)
		.join(", ");

	return hidden > 0 ? `${text} …` : text;
};

const RecipientChip = ({
	type,
	item,
}: {
	type: RecipientType;
	item: string;
}) => {
	const color = RECIPIENT_TYPE_COLOR[type];

	return (
		<Chip
			label={item}
			title={`${type}: ${item}`}
			size="small"
			variant="outlined"
			sx={{
				height: 20,
				color,
				borderColor: alpha(color, 0.5),
				backgroundColor: alpha(color, 0.08),
				"& .MuiChip-label": { px: 0.75, fontSize: "0.7rem" },
			}}
		/>
	);
};

export const RecipientTypeChips = ({
	values,
	maxVisible,
}: {
	values: RecipientValues;
	maxVisible: number;
}) => {
	const { groups, hiddenGroups, hidden } = getGroups(values, maxVisible);

	if (groups.length === 0) {
		return <>-</>;
	}

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				flexWrap: "wrap",
				gap: 0.5,
				py: 0.5,
				width: "100%",
			}}
		>
			{groups.flatMap((group) =>
				group.items.map((item) => (
					<RecipientChip
						key={`${group.type}-${item}`}
						type={group.type}
						item={item}
					/>
				)),
			)}
			{hidden > 0 ? (
				<Tooltip
					title={
						<Box
							sx={{
								display: "flex",
								flexWrap: "wrap",
								gap: 0.5,
							}}
						>
							{hiddenGroups.flatMap((group) =>
								group.items.map((item) => (
									<RecipientChip
										key={`${group.type}-${item}`}
										type={group.type}
										item={item}
									/>
								)),
							)}
						</Box>
					}
					slotProps={{
						tooltip: {
							sx: {
								backgroundColor: "background.paper",
								border: 1,
								borderColor: "divider",
								boxShadow: 3,
								width: "max-content",
								maxWidth: "30rem",
								p: 1,
							},
						},
					}}
				>
					<Chip
						label="…"
						size="small"
						variant="outlined"
						sx={{
							height: 20,
							cursor: "default",
							"& .MuiChip-label": {
								px: 0.75,
								fontSize: "0.7rem",
								fontWeight: 600,
							},
						}}
					/>
				</Tooltip>
			) : null}
		</Box>
	);
};

export const RecipientTypeLegend = () => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			height: "100%",
			minHeight: 32,
			gap: 1.5,
			px: 1.25,
		}}
	>
		<Typography variant="caption" color="textSecondary" fontSize="0.8rem">
			Recipient type:
		</Typography>
		{RECIPIENT_ORDER.map((type) => {
			const color = RECIPIENT_TYPE_COLOR[type];

			return (
				<Box
					key={type}
					sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
				>
					<Box
						sx={{
							width: 12,
							height: 12,
							borderRadius: "100%",
							border: `1px solid ${alpha(color, 0.5)}`,
							backgroundColor: alpha(color, 0.25),
						}}
					/>
					<Typography
						variant="caption"
						fontSize="0.8rem"
						sx={{ color, fontWeight: 600 }}
					>
						{type}
					</Typography>
				</Box>
			);
		})}
	</Box>
);
